import { useCallback, useMemo, useState } from "react";
import { File } from "@ludens-reklame/rubics-v3-sdk/file/types";
import { Form as RubicsForm } from "@ludens-reklame/rubics-v3-sdk/form/types";
import { useStore } from "@ludens-reklame/rubics-v3-react/store/hooks";
import { useInput } from "@ludens-reklame/rubics-v3-react/util/hooks";
import { Busy } from "../../internal-components/Busy.js";
import { Button } from "../../internal-components/Button.js";
import {
  Constrainer,
  HorizontalSpacer,
  VerticalSpacer,
} from "../../internal-components/Layout.js";
import { RichText } from "../../internal-components/RichText.js";
import { renderField } from "./renderFormField.js";
import {
  Wrapper,
  ButtonWrapper,
  Fields,
  Form,
  FormConstrainer,
  Section,
  Sections,
  IntroText,
  ClearFix,
} from "./styles.js";
import { pushToDataLayer } from "../../util/dataLayer.js";
import type { BackgroundColor, ThemeSettings } from "../../types/theme.js";

export interface FormField {
  type?:
    | "text"
    | "email"
    | "phone"
    | "number"
    | "textarea"
    | "checkbox"
    | "select"
    | "hidden"
    | "file"
    | "image"
    | "checkboxgroup";
  name?: string;
  label?: string;
  richLabel?: string;
  placeholder?: string;
  value?: string;
  min?: number;
  limit?: number;
  required?: boolean;
  short?: boolean;
  options?: FormOption[];
  step?: number;
  requirableBy?: string;
  triggeredBy?: string;
}

export interface FormOption {
  label?: string;
  value?: string;
  name?: string;
}

export interface Props {
  title?: string;
  text?: string;
  thankYouTitle?: string;
  thankYouText?: string;
  action?: string;
  form?: RubicsForm;
  fields?: FormField[];
  backgroundColor?: BackgroundColor;
}

export const ContactForm: React.FunctionComponent<Props> = ({
  title,
  text,
  thankYouTitle,
  thankYouText,
  action,
  form,
  fields = [],
  backgroundColor = "surface1",
}) => {
  const store = useStore<ThemeSettings>();
  const [step, setStep] = useState<number>(0);
  const [uploadedImage, setUploadedImage] = useState<File | undefined>(
    undefined
  );
  const defaults: Record<string, string> = {};
  const isActionCorrespondence = (action || "").includes("correspondence");

  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];

    if (!!field.value && !!field.name) {
      defaults[field.name] = field.value;
    }
  }

  const fieldsOnStep = useMemo<FormField[]>(() => {
    return fields.filter((f) => {
      if (f.type === "hidden") {
        return false;
      }

      if (step === 0) {
        return !f.step || f.step === 1;
      } else {
        return f.step === step;
      }
    });
  }, [step, fields]);

  const hasNextStep = useMemo<boolean>(() => {
    return fields.some((f) => f.step !== undefined && f.step > step);
  }, [step, fields]);

  const hasPreviousStep = useMemo<boolean>(() => {
    return step > 1;
  }, [step]);

  const { submit, setField, input, submitting, success, errored } = useInput({
    initialInput: { ...defaults, ...store.context.query },
    url: action || "",
    method: "POST",
    onSuccess: () => {
      const event = new CustomEvent("formSubmitted", {
        detail: { success: true },
      });

      document.dispatchEvent(event);
      pushToDataLayer("formSubmitted", { success: true });
    },
    onError: () => {
      const event = new CustomEvent("formSubmitted", {
        detail: { success: false },
      });

      document.dispatchEvent(event);
      pushToDataLayer("formSubmitted", { success: false });
    },
  });

  const injectImageToMessage = useCallback(() => {
    if (uploadedImage) {
      if (input["message"]) {
        input["message"] =
          input["message"] +
          "<br /><br />" +
          `Vedlagt fil: <a href="${uploadedImage.url}" target="_blank" rel="noopener noreferrer">${uploadedImage.name}</a>`;
      } else {
        input[
          "message"
        ] = `Vedlagt fil: <a href="${uploadedImage.url}" target="_blank" rel="noopener noreferrer">${uploadedImage.name}</a>`;
      }
    }
  }, [input, uploadedImage]);

  if (success) {
    return (
      <VerticalSpacer>
        <HorizontalSpacer>
          <FormConstrainer $bg={backgroundColor}>
            <Constrainer $width="small">
              <h3>{thankYouTitle || "Takk for din henvendelse"}</h3>
              {thankYouText && (
                <RichText dangerouslySetInnerHTML={{ __html: thankYouText }} />
              )}
            </Constrainer>
          </FormConstrainer>
        </HorizontalSpacer>
      </VerticalSpacer>
    );
  }

  return (
    <Wrapper>
      <VerticalSpacer>
        <HorizontalSpacer className="horizontal-spacer">
          <FormConstrainer className="formconstrainer" $bg={backgroundColor}>
            <Constrainer $width="small">
              <Sections>
                <Section>
                  {title || text ? (
                    <IntroText>
                      {!!title && <h3>{title}</h3>}
                      {!!text && (
                        <RichText dangerouslySetInnerHTML={{ __html: text }} />
                      )}
                    </IntroText>
                  ) : null}
                  <Busy $busy={submitting}>
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();

                        if (hasNextStep) {
                          if (step === 0) {
                            setStep(2);
                          } else {
                            setStep(step + 1);
                          }

                          return;
                        }

                        injectImageToMessage();
                        submit();
                      }}
                    >
                      <Fields>
                        {fieldsOnStep.map((field, fieldIndex) =>
                          renderField({
                            field,
                            data: input,
                            setField,
                            setUploadedImage,
                            reactKey: fieldIndex,
                            site: store.site.slug,
                            isActionCorrespondence,
                          })
                        )}
                      </Fields>
                      <ClearFix>
                        {errored && !submitting && (
                          <p className="b2">
                            Noe galt skjedde! Vennligst prøv igjen!
                          </p>
                        )}
                        <ButtonWrapper>
                          {hasPreviousStep && (
                            <Button
                              type="button"
                              $variant="primary"
                              onClick={() => setStep(step - 1)}
                            >
                              Forrige
                            </Button>
                          )}
                          <Button $variant="primary" type="submit">
                            {hasNextStep
                              ? "Neste steg"
                              : submitting
                              ? "Sender…"
                              : form?.submitButtonText || "Send inn"}
                          </Button>
                        </ButtonWrapper>
                      </ClearFix>
                    </Form>
                  </Busy>
                </Section>
              </Sections>
            </Constrainer>
          </FormConstrainer>
        </HorizontalSpacer>
      </VerticalSpacer>
    </Wrapper>
  );
};
